/* ============================================================
   HERCULES — INGREDIENT CAROUSEL JS
   Pairs with: sections/ingredient-carousel.liquid
              assets/ingredient-carousel.css

   Responsibilities:
   - Prev/next arrow buttons scroll the track by one "page"
   - Mouse wheel / trackpad vertical scrolls become horizontal
   - Update arrow disabled state at start/end of scroll
   - Clickable cards (data-lp-ingredient-link) navigate to the
     ingredient's full profile URL
   - Mobile dot indicators highlight the current page
   - No carousel library required — uses native scroll-snap
   ============================================================ */

(function () {
  'use strict';

  function init(section) {
    var track = section.querySelector('[data-lp-track]');
    if (!track) return;

    var prevBtn = section.querySelector('[data-lp-prev]');
    var nextBtn = section.querySelector('[data-lp-next]');
    var dotsContainer = section.querySelector('[data-lp-dots]');
    var items = Array.prototype.slice.call(track.querySelectorAll('.lp-ingredients__item'));

    if (items.length === 0) return;

    /* ----------- helpers ----------- */
    function getStep() {
      if (items.length < 2) return items[0].offsetWidth;
      return items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left;
    }

    function getVisibleCount() {
      var trackWidth = track.clientWidth;
      var step = getStep();
      if (step <= 0) return 1;
      return Math.max(1, Math.round(trackWidth / step));
    }

    function updateNavState() {
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

      if (prevBtn) prevBtn.disabled = atStart;
      if (nextBtn) nextBtn.disabled = atEnd;

      updateDots();
    }

    /* ----------- prev/next arrows ----------- */
    function scrollByPage(direction) {
      var visible = getVisibleCount();
      var step = getStep();
      var distance = Math.max(step, step * Math.max(1, visible - 1)) * direction;
      track.scrollBy({ left: distance, behavior: 'smooth' });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', function () { scrollByPage(-1); });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () { scrollByPage(1); });
    }

    /* ----------- wheel / trackpad horizontal scroll ----------- */
    // Translate vertical wheel deltas into horizontal scroll on the track.
    // Trackpads with native horizontal deltaX pass through untouched.
    var wheelLockUntil = 0;
    track.addEventListener('wheel', function (e) {
      var absX = Math.abs(e.deltaX);
      var absY = Math.abs(e.deltaY);

      // If the user is clearly scrolling vertically (trackpad with x≈0),
      // intercept and remap to horizontal. If they're already scrolling
      // horizontally (deltaX present), let the browser handle it natively.
      if (absY > absX) {
        // Only intercept if we have room to scroll horizontally
        var atStart = track.scrollLeft <= 0 && e.deltaY < 0;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1 && e.deltaY > 0;
        if (atStart || atEnd) return; // let page scroll vertically at edges

        e.preventDefault();
        track.scrollBy({ left: e.deltaY, behavior: 'auto' });
        wheelLockUntil = Date.now() + 150;
      }
    }, { passive: false });

    /* ----------- dots ----------- */
    function buildDots() {
      if (!dotsContainer) return;
      dotsContainer.innerHTML = '';
      var visible = getVisibleCount();
      var pageCount = Math.max(1, Math.ceil(items.length / visible));
      if (pageCount < 2) {
        dotsContainer.style.display = 'none';
        return;
      }
      dotsContainer.style.display = '';
      for (var i = 0; i < pageCount; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'lp-ingredients__dot';
        dot.setAttribute('aria-label', 'Go to page ' + (i + 1));
        dot.dataset.lpDotIndex = String(i);
        dot.addEventListener('click', (function (idx) {
          return function () {
            var step = getStep();
            track.scrollTo({ left: idx * visible * step, behavior: 'smooth' });
          };
        })(i));
        dotsContainer.appendChild(dot);
      }
    }

    function updateDots() {
      if (!dotsContainer) return;
      var dots = dotsContainer.querySelectorAll('.lp-ingredients__dot');
      if (dots.length === 0) return;
      var visible = getVisibleCount();
      var step = getStep();
      if (step <= 0) return;
      var currentPage = Math.round(track.scrollLeft / (step * visible));
      currentPage = Math.max(0, Math.min(currentPage, dots.length - 1));
      for (var i = 0; i < dots.length; i++) {
        dots[i].classList.toggle('is-active', i === currentPage);
      }
    }

    /* ----------- card click navigation ----------- */
    var linkedCards = section.querySelectorAll('[data-lp-ingredient-link]');
    Array.prototype.forEach.call(linkedCards, function (card) {
      var url = card.getAttribute('data-lp-ingredient-link');
      if (!url) return;

      card.addEventListener('click', function () {
        window.location.href = url;
      });

      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'link');
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          window.location.href = url;
        }
      });
    });

    /* ----------- listeners ----------- */
    var scrollTimeout;
    track.addEventListener('scroll', function () {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(updateNavState, 80);
    }, { passive: true });

    var resizeTimeout;
    window.addEventListener('resize', function () {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(function () {
        buildDots();
        updateNavState();
      }, 150);
    });

    /* ----------- initial ----------- */
    buildDots();
    updateNavState();
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-lp-ingredients]');
    Array.prototype.forEach.call(sections, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify section reload in theme editor
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (event) {
      if (event.target && event.target.querySelector('[data-lp-ingredients]')) {
        initAll();
      }
    });
  }
})();